<template>
    <div class="visor-root">

        <!-- Toolbar -->
        <div class="visor-toolbar">
            <div class="row items-center q-gutter-x-sm">
                <q-icon name="mdi-file-pdf-box" size="28px" color="red-4" />
                <div class="column">
                    <span class="text-weight-bold text-grey-9" style="font-size:14px;line-height:1.2">
                        Hoja Clínica Psicología
                    </span>
                    <span class="text-grey-5" style="font-size:11px;line-height:1.2">
                        {{ paciente?.nombres }} {{ paciente?.apellidoPaterno }} {{ paciente?.apellidoMaterno }}
                    </span>
                </div>
            </div>
            <q-space />

            <div v-if="totalPaginas > 0 && tabActivo !== 'seguimientos'" class="row items-center q-gutter-x-sm q-mr-sm">
                <q-btn flat round dense color="grey-7" icon="mdi-chevron-left"
                    :disable="paginaActual <= 1" @click="cambiarPagina(paginaActual - 1)" />
                <span class="text-grey-7" style="font-size:13px">{{ paginaActual }} / {{ totalPaginas }}</span>
                <q-btn flat round dense color="grey-7" icon="mdi-chevron-right"
                    :disable="paginaActual >= totalPaginas" @click="cambiarPagina(paginaActual + 1)" />
            </div>

            <q-btn-group flat v-if="tabActivo !== 'seguimientos'">
                <q-btn flat round color="grey-7" icon="mdi-refresh" :loading="cargando" @click="generarVista()">
                    <q-tooltip>Generar vista previa</q-tooltip>
                </q-btn>
                <q-btn flat round color="grey-7" icon="mdi-download" :disable="!pdfCargado" @click="descargar()">
                    <q-tooltip>Descargar</q-tooltip>
                </q-btn>
                <q-btn flat round color="grey-7" icon="mdi-printer" :disable="!pdfCargado" @click="imprimir()">
                    <q-tooltip>Imprimir</q-tooltip>
                </q-btn>
            </q-btn-group>
            <q-space />

            <q-tabs v-model="tabActivo" dense no-caps
                active-color="primary" indicator-color="primary">
                <q-tab name="hoja" icon="mdi-file-account" label="Hoja Clínica" />
                <q-tab name="seguimientos" icon="mdi-clipboard-list" label="Seguimientos" />
            </q-tabs>
        </div>

        <!-- TAB: Hoja Clínica -->
        <div v-show="tabActivo === 'hoja'" class="visor-body">
            <div v-if="!pdfCargado && !cargando" class="visor-empty">
                <div class="empty-card">
                    <q-icon name="mdi-file-pdf-box" size="80px" color="red-3" />
                    <p class="text-h6 text-grey-7 q-mt-md q-mb-xs">Sin vista previa</p>
                    <p class="text-grey-5 text-center q-mb-lg" style="max-width:280px">
                        Genera el documento para previsualizarlo aquí.
                    </p>
                    <q-btn unelevated color="primary" icon="mdi-file-eye"
                        label="Generar vista previa" rounded @click="generarVista()" />
                </div>
            </div>
            <div v-if="cargando" class="visor-empty">
                <div class="empty-card">
                    <q-spinner-dots color="primary" size="60px" />
                    <p class="text-grey-6 q-mt-md">Generando documento...</p>
                </div>
            </div>
            <div v-show="pdfCargado && !cargando" class="visor-pdf-wrapper">
                <div class="pdf-shadow-container">
                    <canvas ref="pdfCanvas" />
                </div>
            </div>
        </div>

        <!-- TAB: Seguimientos -->
        <div v-show="tabActivo === 'seguimientos'" class="visor-body">
            <div class="row" style="height:100%">

                <div class="col-3 bg-white border-right" style="overflow-y:auto">
                    <div class="q-pa-sm q-gutter-y-xs">
                        <div class="text-caption text-grey-5 q-px-sm q-pt-sm q-pb-xs">
                            CONSULTAS ({{ consultas.length }})
                        </div>
                        <q-item v-for="(c, i) in consultas" :key="c.id ?? i" clickable v-ripple
                            :active="consultaSeleccionada?.id === c.id"
                            active-class="bg-primary text-white"
                            class="rounded-borders q-mb-xs"
                            @click="seleccionarConsulta(c)">
                            <q-item-section avatar>
                                <q-icon name="mdi-clipboard-text" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label class="text-weight-medium">Consulta {{ i + 1 }}</q-item-label>
                                <q-item-label caption>{{ c.fecha ?? 'Sin fecha' }}</q-item-label>
                            </q-item-section>
                            <q-item-section side>
                                <q-icon name="mdi-chevron-right" />
                            </q-item-section>
                        </q-item>
                        <div v-if="consultas.length === 0"
                            class="column items-center text-grey-4 q-pa-lg q-gutter-y-sm">
                            <q-icon name="mdi-clipboard-off" size="40px" />
                            <span class="text-caption">Sin consultas registradas</span>
                        </div>
                    </div>
                </div>

                <div class="col bg-grey-2 column" style="overflow:hidden; position:relative">
                    <div v-if="!consultaSeleccionada && !cargandoSeguimiento" class="visor-empty">
                        <div class="empty-card">
                            <q-icon name="mdi-cursor-pointer" size="64px" color="grey-3" />
                            <p class="text-grey-5 q-mt-md">Selecciona una consulta para ver el documento</p>
                        </div>
                    </div>
                    <div v-if="cargandoSeguimiento" class="visor-empty">
                        <div class="empty-card">
                            <q-spinner-dots color="primary" size="60px" />
                            <p class="text-grey-6 q-mt-md">Generando seguimiento...</p>
                        </div>
                    </div>
                    <div v-show="consultaSeleccionada && !cargandoSeguimiento" class="visor-pdf-wrapper">
                        <div v-if="totalPaginasSeg > 1"
                            class="row items-center justify-center q-gutter-x-sm q-mb-sm">
                            <q-btn flat round dense color="grey-7" icon="mdi-chevron-left"
                                :disable="paginaActualSeg <= 1" @click="cambiarPaginaSeg(paginaActualSeg - 1)" />
                            <span class="text-grey-7" style="font-size:13px">
                                {{ paginaActualSeg }} / {{ totalPaginasSeg }}
                            </span>
                            <q-btn flat round dense color="grey-7" icon="mdi-chevron-right"
                                :disable="paginaActualSeg >= totalPaginasSeg" @click="cambiarPaginaSeg(paginaActualSeg + 1)" />
                        </div>

                            <q-btn flat round dense color="grey-7" icon="mdi-download" @click="descargarSeguimiento()">
                                <q-tooltip>Descargar</q-tooltip>
                            </q-btn>
                        <div class="pdf-shadow-container">
                            <canvas ref="pdfCanvasSeg" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick, onBeforeMount, onMounted } from "vue"
import { useStore } from "vuex"
import { generarHojaPsicologiaSeguimiento } from "../PDF/HojaClinicaPsicologicaSeguimiento.js"
import { generarHojaPsicologia } from "../PDF/HojaClinicaPsicologica.js"
import * as pdfjsLib from "pdfjs-dist"
import { useQuasar, Notify, QSpinnerFacebook } from "quasar"
import axios from "axios"
pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`

const $q = useQuasar()
const store = useStore()

const tabActivo = ref('hoja')
const paciente = computed(() => store.state.pacienteStore)
const consultas = ref([]);
const consultaPsico = ref({});
const listaPersonal = ref([]);

const pdfCanvas = ref(null)
const pdfCargado = ref(false)
const cargando = ref(false)
const paginaActual = ref(1)
const totalPaginas = ref(0)
const base64Guardado = ref(null)
let pdfDoc = null

const pdfCanvasSeg = ref(null)
const consultaSeleccionada = ref(null)
const cargandoSeguimiento = ref(false)
const paginaActualSeg = ref(1)
const totalPaginasSeg = ref(0)
const base64GuardadoSeg = ref(null)
let pdfDocSeg = null

onBeforeMount(() => { getObtenerConsultaPsicologicaSeguimientoXPaciente() })

onMounted(async () => {
    await getPersonal();
})
const getPersonal = async () => {
    try {
        const response = await axios.get('/Usuarios/activos')
        console.log('Respuesta backend:', response.data)
        let listaMedicos = response.data.filter(x=> x.rol == 'Médico')
        listaPersonal.value = listaMedicos.map(proveedor => ({
            ...proveedor,
            texto: `${proveedor.nombre} ${proveedor.apellidoPaterno} ${proveedor.apellidoMaterno}`
        }))

    } catch (error) {
        console.error(error)
        Notify.create({
            message: 'Error al consultar la lista de Doctores',
            color: 'negative',
            position: 'top-right'
        })
    } finally {
    }
}
const getCita = async (idCita) => {
  try {
        const response = await axios.get('Cita/' + idCita)
        console.log('cita', response)
        return response.data
     
  } catch (error) {
      console.error(error)
      Notify.create({ message: 'Error al consultar la información', color: 'negative', position: 'top-right' })
  } 
}

const getObtenerConsultaPsicologicaXPaciente = async () => {
  $q.loading.show({ spinner: QSpinnerFacebook, spinnerColor: 'primary', spinnerSize: 100, message: 'Consultando Consulta...' })
  try {
      const response = await axios.get('ConsultaPsicologica/ObtenerConsultaPsicologicaXPaciente/' + paciente.value.id )

        consultaPsico.value = response.data

      console.log('consulta',response.data)
  } catch (error) {
      console.error(error)
      Notify.create({ message: 'Error al consultar la información', color: 'negative', position: 'top-right' })
  } finally {
      $q.loading.hide()
  }
}

const getObtenerConsultaPsicologicaSeguimientoXPaciente = async () => {
  $q.loading.show({ spinner: QSpinnerFacebook, spinnerColor: 'primary', spinnerSize: 100, message: 'Consultando consultas...' })
  try {
      const response = await axios.get('ConsultaPsicologicaSeguimiento/ObtenerConsultaPsicologicaSeguimientoXPaciente/' + paciente.value.id)
      consultas.value = response.data
      console.log(consultas)
  } catch (error) {
      console.error(error)
      Notify.create({ message: 'Error al consultar la información', color: 'negative', position: 'top-right' })
  } finally {
      $q.loading.hide()
  }
}

async function generarVista() {
    try {
        cargando.value = true
        pdfDoc = null
        pdfCargado.value = false

        await getObtenerConsultaPsicologicaXPaciente();
        
        if(consultaPsico.value == ''){
            $q.notify({ type: 'info', message: 'El paciente no cuenta con consultas piscológicas.', position:'top-right' })
            return
        }
        console.log(consultaPsico.value)
        let cita = await getCita(consultaPsico.value.idCita);
        console.log('cita 2',cita)
        console.log('lista personal',listaPersonal)
        console.log('idpaciente',consultaPsico.value.idPaciente)
        let medico = listaPersonal.value.find(x => x.id == cita.doctorId && x.rol == 'Médico')
        
        const base64 = await generarHojaPsicologia(paciente.value, consultaPsico.value, medico.cedula)
        base64Guardado.value = base64

        const limpio = base64.replace('data:application/pdf;filename=generated.pdf;base64,', '')
        pdfDoc = await pdfjsLib.getDocument({ data: atob(limpio) }).promise

        totalPaginas.value = pdfDoc.numPages
        paginaActual.value = 1

        pdfCargado.value = true
        await nextTick()
        await renderPagina(1)
    } catch (e) {
        console.error(e)
        $q.notify({ type: 'negative', message: 'Error al generar el PDF' })
    } finally {
        cargando.value = false
    }
}

async function renderPagina(num) {
    if (!pdfDoc || !pdfCanvas.value) return
    const page = await pdfDoc.getPage(num)
    const scale = 2
    const scaled = page.getViewport({ scale })
    const canvas = pdfCanvas.value
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    canvas.width = scaled.width
    canvas.height = scaled.height
    await page.render({ canvasContext: ctx, viewport: scaled }).promise
}

async function cambiarPagina(num) {
    paginaActual.value = num
    await renderPagina(num)
}

function descargar() {
    if (!base64Guardado.value) return
    const nombre = `HojaPsicologia_${paciente.value?.nombres ?? ''}_${paciente.value?.apellidoPaterno ?? ''}.pdf`
    const a = document.createElement('a')
    a.href = base64Guardado.value
    a.download = nombre
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

function imprimir() {
    if (!base64Guardado.value) return
    const limpio = base64Guardado.value.replace('data:application/pdf;filename=generated.pdf;base64,', '')
    const blob = new Blob([Uint8Array.from(atob(limpio), c => c.charCodeAt(0))], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url)
    win.onload = () => { win.print(); URL.revokeObjectURL(url) }
}

async function seleccionarConsulta(consulta) {
    console.log(consulta)
    consultaSeleccionada.value = consulta
    try {
        cargandoSeguimiento.value = true
        pdfDocSeg = null

        let cita = await getCita(consultaSeleccionada.value.idCita);
        let medico = listaPersonal.value.find(x => x.id == cita.doctorId && x.rol == 'Médico')

        const base64 = await generarHojaPsicologiaSeguimiento(paciente.value, consulta, medico)
        base64GuardadoSeg.value = base64

        const limpio = base64.replace('data:application/pdf;filename=generated.pdf;base64,', '')
        pdfDocSeg = await pdfjsLib.getDocument({ data: atob(limpio) }).promise

        totalPaginasSeg.value = pdfDocSeg.numPages
        paginaActualSeg.value = 1

        cargandoSeguimiento.value = false
        await nextTick()
        await renderPaginaSeg(1)
    } catch (e) {
        console.error(e)
        $q.notify({ type: 'negative', message: 'Error al generar seguimiento' })
        cargandoSeguimiento.value = false
    }
}

async function renderPaginaSeg(num) {
    if (!pdfDocSeg || !pdfCanvasSeg.value) return
    const page = await pdfDocSeg.getPage(num)
    const scale = 2
    const scaled = page.getViewport({ scale })
    const canvas = pdfCanvasSeg.value
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    canvas.width = scaled.width
    canvas.height = scaled.height
    await page.render({ canvasContext: ctx, viewport: scaled }).promise
}

async function cambiarPaginaSeg(num) {
    paginaActualSeg.value = num
    await renderPaginaSeg(num)
}

function descargarSeguimiento() {
    if (!base64GuardadoSeg.value || !consultaSeleccionada.value) return
    const nombre = `Seguimiento_${consultaSeleccionada.value.fecha ?? 'sin_fecha'}_${paciente.value?.apellidoPaterno ?? ''}.pdf`
    const a = document.createElement('a')
    a.href = base64GuardadoSeg.value
    a.download = nombre
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

function formatearFechaHora(fecha) {
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(fecha))
}

onUnmounted(() => {
    pdfDoc?.destroy()
    pdfDocSeg?.destroy()
})
</script>

<style scoped>
.visor-root { display: flex; flex-direction: column; height: 100vh; background: #f0f2f5; }
.visor-toolbar { display: flex; align-items: center; padding: 12px 20px; background: #fff; border-bottom: 1px solid #e8eaed; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; }
.visor-body { flex: 1; overflow: hidden; position: relative; }
.visor-empty { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.empty-card { display: flex; flex-direction: column; align-items: center; background: #fff; border-radius: 16px; padding: 48px 40px; box-shadow: 0 4px 24px rgba(0,0,0,.07); }
.visor-pdf-wrapper { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; padding: 24px; overflow-y: auto; overflow-x: hidden; }
.pdf-shadow-container { border-radius: 8px; overflow: hidden; box-shadow: 0 4px 32px rgba(0,0,0,.15); flex-shrink: 0; width: 100%; }
canvas { display: block; width: 100% !important; height: auto !important; }
.border-right { border-right: 1px solid #e8eaed; }
canvas { display: block; }
.fade-enter-active, .fade-leave-active { transition: opacity .25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-up-enter-active { transition: all .35s cubic-bezier(.16,1,.3,1); }
.slide-up-enter-from { opacity: 0; transform: translateY(20px); }
</style>